import { useSyncExternalStore } from 'react';
import {
  clearActiveTeam,
  getActiveTeam,
  setActiveTeam,
  subscribeActiveTeam,
  type ActiveTeam,
} from './activeTenant';

/**
 * React binding over the pure-TS {@link activeTenant} store. Components consume
 * the active team ONLY through this hook (never the module directly), so they
 * re-render when the selection changes — including cross-tab changes.
 *
 * `getActiveTeam` returns a stable reference until the selection changes, which
 * satisfies `useSyncExternalStore`'s snapshot-stability requirement.
 */
export interface UseActiveTeamResult {
  /** The active team, or null when none is chosen (the gate is shown). */
  activeTeam: ActiveTeam | null;
  /** Persists and activates a team (enters the app under its tenant bundle). */
  setActiveTeam: (team: ActiveTeam) => void;
  /** Clears the active team (returns to the tenant gate). */
  clearActiveTeam: () => void;
}

export function useActiveTeam(): UseActiveTeamResult {
  const activeTeam = useSyncExternalStore(
    subscribeActiveTeam,
    getActiveTeam,
    getActiveTeam,
  );
  return { activeTeam, setActiveTeam, clearActiveTeam };
}
